import React, { useEffect } from "react"
import type { BoxProps } from "@chakra-ui/react"
import { Flex, Box, forwardRef } from "@chakra-ui/react"
import { Link } from "components/Link"
import { useScreenViewport } from "lib/checkViewport"
import type { ImageProps } from "next/image"
import { useRouter } from "lib/router"
import { useInView } from "react-intersection-observer"
import type { VariantType } from "@shared/graphql.types"
import { getPDPUrl } from "utils/getPDPUrl"
import { productClick } from "utils/gtm"
import { productImpression } from "utils/gtm/impressions"
import { trackClick } from "tracking"
import { siteUrl } from "utils/getSiteUrl"
import { useCurrencyName } from "hooks/useCurrencyName"

import { useLandingPageContext } from "packages/acquisition/components/chakra-components/LandingPage/LandingPageProvider"
import { useVariantIds } from "hooks/useVariantIds"
import { useUserV2, useLocale } from "hooks"
import { locationTypes } from "components/BasketLists/BasketList.utils"
import { cmsPageClick } from "packages/acquisition/components/chakra-components/LandingPage/LandingPage.utils"
import type { CMSPageModule } from "packages/acquisition/components/chakra-components/LandingPage/LandingPage.utils"
import { ProductCardImage } from "./ProductCardImage"
import { ProductCardRating } from "./ProductCardRating"
import { ProductCardText } from "./ProductCardText"
import { QuickShop } from "./QuickShop"
import { GiftMembershipProductCard } from "./GiftMembershipProductCard"
import type { SegmentTrackProductClickedEvent } from "@beautypie/segment/Track/ProductClicked"
import { useCurrencyCode } from "hooks/useCurrencyCode"
import { getRegion } from "@shared/utils.locale"

type Props = {
  variant: VariantType
  currency?: string
  position?: number
  showPrices?: boolean
  setConfirmationVariant?: React.Dispatch<
    React.SetStateAction<VariantType | undefined>
  >
  showQuickShopButton?: boolean
  setConfirmation?: React.Dispatch<React.SetStateAction<boolean>>
  trackSearch?: (slug: string) => void
  trackViewedVariant?: (variantSku: string) => void
  children?: React.ReactNode
  onClick?: () => void
  isATBConfirmation?: boolean
  isQuizResultPage?: boolean
  trackingModule?: Omit<CMSPageModule, "moduleClickUrl">
  resultType?: SegmentTrackProductClickedEvent["properties"]["result_type"]
  searchTerm?: string
} & Pick<ImageProps, "objectFit" | "priority" | "sizes">

type ProductImageProps = {
  variant: VariantType
  position: number
  imageProps: Pick<ImageProps, "objectFit" | "priority" | "sizes">
  stylingProps: Pick<ImageProps, "objectFit" | "priority" | "sizes">
  giftMembershipProductId: string
}

export const ProductImageWrapper = ({
  variant,
  position,
  imageProps,
  stylingProps,
  giftMembershipProductId,
}: ProductImageProps) => {
  const { media, detailsMedia } = variant
  const isGiftMembership = variant.id === giftMembershipProductId
  const [firstMediaImage] = media || []
  const ctBynderImage = detailsMedia ?? firstMediaImage

  if (isGiftMembership) {
    const giftMembershipImageSrc = variant?.visuals?.[0]?.url
    return (
      <GiftMembershipProductCard
        imageSrc={giftMembershipImageSrc}
        props={stylingProps}
      />
    )
  }

  if (ctBynderImage) {
    return (
      <ProductCardImage
        variant={variant}
        image={ctBynderImage}
        position={position}
        {...imageProps}
      />
    )
  }

  return null
}

export const ProductCard: React.FC<Props> = ({
  variant,
  position = 0,
  setConfirmationVariant,
  setConfirmation,
  showQuickShopButton,
  trackSearch,
  trackViewedVariant,
  onClick,
  isATBConfirmation = false,
  trackingModule,
  resultType,
  searchTerm,
  ...imageProps
}) => {
  const user = useUserV2()
  const { pageId } = useLandingPageContext()
  const { giftMembershipProductId } = useVariantIds()
  const { isMobile, isTablet } = useScreenViewport()

  const tags = [] // Add them to the variants
  const isLandingPage = !!pageId

  const renderInMobileAndTablet = isMobile || isTablet
  const showMobileQuickShopButton =
    user.hasMembership &&
    isLandingPage &&
    showQuickShopButton &&
    renderInMobileAndTablet
  const showDesktopQuickShopButton =
    user.hasMembership &&
    !isLandingPage &&
    showQuickShopButton &&
    !renderInMobileAndTablet

  const css =
    showDesktopQuickShopButton && variant.availability.status !== "outofstock"
      ? {
          "#quickShopDesktopButton": {
            transition: "all 0.4s ease",
            opacity: 0,
          },
          "#productCardContent": {
            transition: "all 0.4s ease",
            opacity: 1,
          },
          transition: "all 0.4s ease",
          "&:hover,&:focus,&:active": {
            "#quickShopDesktopButton": {
              transition: "all 0.4s ease",
              opacity: 1,
            },
            "#productCardContent": {
              transition: "all 0.4s ease",
              opacity: 0.6,
            },
          },
        }
      : {}

  const stylingProps = {
    ...imageProps,
    sizes:
      "(max-width: 374px) 100vw, (max-width: 768px) 50vw, (max-width: 1440px) 33vw, 20vw",
  }

  return (
    <ProductCardLayout
      variant={variant}
      position={position}
      onClick={onClick}
      isATBConfirmation={isATBConfirmation}
      trackingModule={trackingModule}
      resultType={resultType}
      searchTerm={searchTerm}
      {...(trackSearch && { trackSearch })}
      {...(trackViewedVariant && { trackViewedVariant })}
    >
      {showDesktopQuickShopButton ? (
        <Box css={css} h="100%">
          <Flex id="productCardContent" h="100%" flexDirection="column">
            <ProductImageWrapper
              variant={variant}
              position={position}
              imageProps={imageProps}
              stylingProps={stylingProps}
              giftMembershipProductId={giftMembershipProductId}
            />
            <Flex
              pt={1}
              pb={-1}
              flexGrow={1}
              flexDirection="column"
              justifyContent="space-between"
            >
              <Box>
                <ProductCardText variant={variant} tags={tags} />
              </Box>
            </Flex>
          </Flex>

          {showDesktopQuickShopButton &&
            variant.availability.status !== "outofstock" && (
              <Box
                id="quickShopDesktopButton"
                display="flex"
                position="absolute"
                top={0}
                alignItems="center"
                justifyContent="center"
                width="100%"
                pb="100%"
                pointerEvents="none"
              >
                <QuickShop
                  variant={variant}
                  buttonVariant="atbDesktop"
                  setConfirmationVariant={setConfirmationVariant}
                  setConfirmation={setConfirmation}
                />
              </Box>
            )}
        </Box>
      ) : (
        <>
          <ProductImageWrapper
            variant={variant}
            position={position}
            imageProps={imageProps}
            stylingProps={stylingProps}
            giftMembershipProductId={giftMembershipProductId}
          />
          <Flex
            pt={1}
            pb={{ base: -1, sm: 1 }}
            flexGrow={1}
            flexDirection="column"
            justifyContent="space-between"
          >
            <Box>
              <ProductCardRating variant={variant} />
              <ProductCardText variant={variant} tags={tags} />
            </Box>
          </Flex>

          {showMobileQuickShopButton && (
            <QuickShop
              variant={variant}
              buttonVariant="atbMobile"
              setConfirmationVariant={setConfirmationVariant}
              setConfirmation={setConfirmation}
            />
          )}
        </>
      )}
    </ProductCardLayout>
  )
}

type LinkProps = {
  onMouseEnter:
    | React.MouseEventHandler<HTMLAnchorElement | HTMLDivElement>
    | undefined
  onMouseLeave:
    | React.MouseEventHandler<HTMLAnchorElement | HTMLDivElement>
    | undefined
}

type ProductCardLayoutBoxProps = BoxProps & {
  id: string
  outOfStock: boolean
  pdpUrlLink: string
  css: unknown
  linkTitle: string
  variant: VariantType
  variantPosition: number
  trackSearch?: (slug: string) => void
  isATBConfirmation: boolean
  trackingModule?: Omit<CMSPageModule, "moduleClickUrl">
  resultType?: ResultType
  searchTerm?: string
}
type ResultType = SegmentTrackProductClickedEvent["properties"]["result_type"]

export const ProductCardLayoutBox = forwardRef<
  ProductCardLayoutBoxProps,
  "div"
>((props, ref) => {
  const locale = useLocale()
  const { variant, trackingModule, resultType, searchTerm } = props
  const {
    title: name,
    subTitle: vrt,
    brandName: brand,
    sku,
    pricing: { members, typical },
  } = props.variant

  const currency = useCurrencyName()
  const currencyCode = useCurrencyCode()
  const clickUrl = getPDPUrl(variant, locale)

  const linkProps: LinkProps = {
    onMouseEnter: props.onMouseEnter,
    onMouseLeave: props.onMouseLeave,
  }

  const resultTypes: ResultType[] = [
    "browse",
    "recommendation",
    "search",
    "suggestions",
  ]
  const isResultTypeValid = resultType && resultTypes.includes(resultType)

  return (
    <Link
      ref={ref}
      key={props.id}
      w="full"
      data-testid="ProductCard"
      overflow="hidden"
      href={props.pdpUrlLink}
      title={props.linkTitle}
      mb={{ base: 1, lg: 0 }}
      onClick={(e) => {
        const { trackSearch, variantPosition } = props
        productClick(variant, variantPosition, "text", currencyCode)
        trackClick(
          {
            event: "Product Clicked",
            properties: {
              sku,
              name,
              members_price: members,
              typical_price: typical,
              url: `${siteUrl}${props.pdpUrlLink}`,
              currency,
              brand,
              variant: vrt ?? "",
              location_type:
                locationTypes[
                  props.isATBConfirmation ? "atbConfirmation" : "default"
                ],
              ...(isResultTypeValid && { result_type: resultType }),
            },
          },
          {
            search: {
              term: searchTerm,
            },
          }
        )
        if (trackingModule) {
          cmsPageClick({
            ...trackingModule,
            moduleContentUrl: clickUrl,
          })
        }
        if (trackSearch && variant?.slug) trackSearch(variant.slug)
        if (props.onClick) props.onClick(e)
      }}
      {...linkProps}
    >
      <Box
        position="relative"
        height="100%"
        flexDirection="column"
        css={props.css}
      >
        {props.children}
      </Box>
    </Link>
  )
})

type ProductCardLayoutProps = {
  variant: VariantType
  position: number
  trackSearch?: (slug: string) => void
  trackViewedVariant?: (variantSku: string) => void
  children?: React.ReactNode
  onClick?: () => void
  isATBConfirmation: boolean
  trackingModule?: Omit<CMSPageModule, "moduleClickUrl">
  resultType?: ResultType
  searchTerm?: string
}

export const ProductCardLayout: React.FC<ProductCardLayoutProps> = ({
  children,
  variant,
  position,
  trackSearch,
  trackViewedVariant,
  onClick,
  isATBConfirmation,
  trackingModule,
  resultType,
  searchTerm,
}) => {
  const { id, availability, brandName, title, subTitle } = variant
  const outOfStock = availability?.status === "outofstock"
  const { asPath } = useRouter()
  const currencyCode = useCurrencyCode()
  const region = getRegion()
  const pdpUrlLink = getPDPUrl(variant, region)

  const variantBrandName = `${brandName?.length ? `${brandName} - ` : ""}`
  const variantSubtitle = `${subTitle?.length ? ` - ${subTitle}` : ""}`
  const linkTitle = `${variantBrandName}${title}${variantSubtitle}`

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 1,
  })

  useEffect(() => {
    if (inView) {
      // Fire a product impression event
      productImpression({ variant, position, path: asPath }, currencyCode)
      if (trackViewedVariant) trackViewedVariant(variant.sku)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, currencyCode])

  const css = {
    display: "flex",
    alignItems: "stretch",
    "&:hover": {
      cursor: "pointer",
    },
    "&:active": {
      cursor: "pointer",
    },
  }
  return (
    <ProductCardLayoutBox
      id={id || "no-id"}
      ref={ref}
      outOfStock={outOfStock}
      pdpUrlLink={pdpUrlLink}
      css={css}
      linkTitle={linkTitle}
      variant={variant}
      variantPosition={position}
      onClick={onClick}
      isATBConfirmation={isATBConfirmation}
      trackingModule={trackingModule}
      resultType={resultType}
      searchTerm={searchTerm}
      {...(trackSearch && { trackSearch })}
    >
      {children}
    </ProductCardLayoutBox>
  )
}
